import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { Button, Divider, InputNumber, message, Modal, Select, Spin } from 'antd';
import { ReminderSettings } from 'types/calendarSetting';
import { updateReminderSettings } from 'api/calendarSetting';

interface MinimumTimeProps {
    onUpdate: () => void;
    value?: ReminderSettings;
}

type TimeUnit = 'minutes' | 'hours' | 'days';

const PRESET_OPTIONS = [
    { label: 'No restriction', value: 0 },
    { label: '1 hour', value: 1 },
    { label: '2 hours', value: 2 },
    { label: '3 hours', value: 3 },
    { label: '4 hours', value: 4 },
];

const CUSTOM_SENTINEL = -1;

const TOAST_MS = 5000;

const getUnitLabel = (value: number, unit: TimeUnit): string => {
    const unitLabels: Record<TimeUnit, [string, string]> = {
        minutes: ['minute', 'minutes'],
        hours: ['hour', 'hours'],
        days: ['day', 'days'],
    };
    const [singular, plural] = unitLabels[unit];
    return value === 1 ? singular : plural;
};

const hoursToDisplayLabel = (hours: number): string => {
    if (hours === 0) return '';
    if (hours >= 24 && hours % 24 === 0) {
        const days = hours / 24;
        return `${days} ${getUnitLabel(days, 'days')}`;
    }
    if (hours < 1 && hours > 0) {
        const mins = Math.round(hours * 60);
        return `${mins} ${getUnitLabel(mins, 'minutes')}`;
    }
    return `${hours} ${getUnitLabel(hours, 'hours')}`;
};

const detectUnitFromHours = (hours: number): { value: number; unit: TimeUnit } => {
    if (hours >= 24 && hours % 24 === 0) {
        return { value: hours / 24, unit: 'days' };
    }
    if (hours < 1 && hours > 0) {
        return { value: Math.round(hours * 60), unit: 'minutes' };
    }
    return { value: hours, unit: 'hours' };
};

const convertToHours = (value: number, unit: TimeUnit): number => {
    switch (unit) {
        case 'minutes':
            return value / 60;
        case 'hours':
            return value;
        case 'days':
            return value * 24;
        default:
            return value;
    }
};

const getMaxForUnit = (unit: TimeUnit): number => {
    switch (unit) {
        case 'minutes':
            return 1440;
        case 'days':
            return 30;
        default:
            return 240;
    }
};

/** Figma 4172:61351 — prefill when opening custom modal */
const getModalInitialState = (hours: number): { amount: number; unit: TimeUnit } => {
    const preset = PRESET_OPTIONS.find((opt) => opt.value === hours);
    if (hours > 0 && !preset) {
        return detectUnitFromHours(hours);
    }
    if (preset && hours > 0) {
        return { amount: hours, unit: 'hours' };
    }
    return { amount: 2, unit: 'hours' };
};

const MinimumTime = (props: MinimumTimeProps) => {
    const { onUpdate, value } = props;
    const [selectValue, setSelectValue] = React.useState(0);
    const [loading, setLoading] = React.useState(false);

    const [customModalOpen, setCustomModalOpen] = React.useState(false);
    const [modalAmount, setModalAmount] = React.useState<number | null>(2);
    const [modalUnit, setModalUnit] = React.useState<TimeUnit>('hours');
    const [modalSaving, setModalSaving] = React.useState(false);

    const [toastVisible, setToastVisible] = React.useState(false);
    const previousHoursRef = React.useRef(0);
    const toastTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

    const clearToastTimer = React.useCallback(() => {
        if (toastTimerRef.current) {
            clearTimeout(toastTimerRef.current);
            toastTimerRef.current = undefined;
        }
    }, []);

    const showSuccessToast = React.useCallback(() => {
        clearToastTimer();
        setToastVisible(true);
        toastTimerRef.current = setTimeout(() => setToastVisible(false), TOAST_MS);
    }, [clearToastTimer]);

    React.useEffect(() => {
        return () => clearToastTimer();
    }, [clearToastTimer]);

    React.useEffect(() => {
        const hours = value?.minAdvanceBookingHours || 0;
        const preset = PRESET_OPTIONS.find((opt) => opt.value === hours);
        if (preset) {
            setSelectValue(hours);
        } else if (hours > 0) {
            setSelectValue(hours);
        } else {
            setSelectValue(0);
        }
    }, [value]);

    const openCustomModal = React.useCallback(() => {
        const hours = value?.minAdvanceBookingHours || 0;
        const initial = getModalInitialState(hours);
        setModalAmount(initial.amount);
        setModalUnit(initial.unit);
        setCustomModalOpen(true);
    }, [value]);

    const closeCustomModal = React.useCallback(() => {
        setCustomModalOpen(false);
    }, []);

    const buildSelectOptions = () => {
        const opts = [...PRESET_OPTIONS];
        const currentHours = value?.minAdvanceBookingHours || 0;
        const isPreset = PRESET_OPTIONS.some((opt) => opt.value === currentHours);
        if (currentHours > 0 && !isPreset) {
            opts.push({
                label: hoursToDisplayLabel(currentHours),
                value: currentHours,
            });
        }
        opts.push({ label: 'Custom', value: CUSTOM_SENTINEL });
        return opts;
    };

    const saveToServer = async (hours: number) => {
        previousHoursRef.current = value?.minAdvanceBookingHours ?? 0;
        setLoading(true);
        const result = await updateReminderSettings({ minAdvanceBookingHours: hours });
        setLoading(false);
        if (!result.error) {
            showSuccessToast();
            onUpdate();
        } else {
            message.error('Could not update settings. Please try again.');
        }
    };

    const handleSelectChange = async (val: number) => {
        if (val === CUSTOM_SENTINEL) {
            openCustomModal();
            return;
        }
        setSelectValue(val);
        await saveToServer(val);
    };

    const handleModalSave = async () => {
        if (modalAmount == null || modalAmount <= 0) {
            message.warning('Please enter a valid number');
            return;
        }
        const hours = convertToHours(modalAmount, modalUnit);
        previousHoursRef.current = value?.minAdvanceBookingHours ?? 0;
        setModalSaving(true);
        const result = await updateReminderSettings({ minAdvanceBookingHours: hours });
        setModalSaving(false);
        if (!result.error) {
            setCustomModalOpen(false);
            showSuccessToast();
            onUpdate();
        } else {
            message.error('Could not update settings. Please try again.');
        }
    };

    const handleUndo = async () => {
        clearToastTimer();
        setToastVisible(false);
        const revertTo = previousHoursRef.current;
        setLoading(true);
        const result = await updateReminderSettings({ minAdvanceBookingHours: revertTo });
        setLoading(false);
        if (!result.error) {
            onUpdate();
        } else {
            message.error('Could not undo. Please try again.');
        }
    };

    const displayHours = selectValue;
    const confirmationLabel = displayHours > 0 ? hoursToDisplayLabel(displayHours) : '';

    return (
        <div className={s.wrap}>
            <div className="text-gray-800 text-xl font-medium">Minimum time in advance</div>
            <Divider />
            <div className={s.tips}>
                Set how many hours in advance patients need to book, so you have time to prepare between appointments
            </div>

            <div>
                <Spin spinning={loading}>
                    <div className={s.item}>
                        <Select
                            loading={loading}
                            value={selectValue}
                            onChange={handleSelectChange}
                            style={{ width: '250px', marginTop: '16px' }}
                        >
                            {buildSelectOptions().map((item) => (
                                <Select.Option key={item.value} value={item.value}>
                                    {item.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </Spin>
                {confirmationLabel && (
                    <div className={s.subTips}>Patients must book at least {confirmationLabel} in advance</div>
                )}
            </div>

            <Modal
                open={customModalOpen}
                onCancel={closeCustomModal}
                footer={null}
                width={510}
                destroyOnClose
                closable
                className={s.customModal}
                title={<span className={s.modalTitle}>Custom minimum time in advance</span>}
            >
                <div className={s.modalBody}>
                    <div className={s.modalSentence}>
                        <span className={s.modalSentenceText}>Patients need to book</span>
                        <InputNumber
                            className={s.modalAmount}
                            value={modalAmount ?? undefined}
                            min={1}
                            max={getMaxForUnit(modalUnit)}
                            controls={false}
                            onChange={(v) => setModalAmount(v === null || v === undefined ? null : v)}
                        />
                        <Select
                            className={s.modalUnitSelect}
                            value={modalUnit}
                            onChange={(u: TimeUnit) => setModalUnit(u)}
                        >
                            <Select.Option value="minutes">Minutes</Select.Option>
                            <Select.Option value="hours">Hours</Select.Option>
                            <Select.Option value="days">Days</Select.Option>
                        </Select>
                        <span className={s.modalSentenceText}>in advance</span>
                    </div>
                </div>
                <div className={s.modalFooter}>
                    <Button onClick={closeCustomModal}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={modalSaving}
                        onClick={handleModalSave}
                        disabled={modalAmount == null || modalAmount <= 0}
                    >
                        Save
                    </Button>
                </div>
            </Modal>

            {toastVisible && (
                <div className={s.toastWrap} role="status">
                    <span className={s.toastMessage}>Minimum time in advance updated successfully</span>
                    <button type="button" className={s.toastUndo} onClick={handleUndo}>
                        Undo
                    </button>
                    <button
                        type="button"
                        className={s.toastClose}
                        onClick={() => {
                            clearToastTimer();
                            setToastVisible(false);
                        }}
                        aria-label="Dismiss"
                    >
                        <CloseOutlined />
                    </button>
                </div>
            )}
        </div>
    );
};

export default MinimumTime;
